import * as PopoverPrimitive from '@radix-ui/react-popover';
import classNames from 'classnames';
import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';
import { useIsMounted } from '~/hooks/useIsMounted';

export type PopoverPlacementSide = 'top' | 'right' | 'bottom' | 'left';

interface Props {
  /** Given a function, it receives the popover's open state so the trigger can render an open-vs-closed look. */
  trigger: React.ReactElement | ((open: boolean) => React.ReactElement);
  children: (closePopover: () => void) => ReactNode;
  container?: HTMLDivElement | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  usePortal?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Called with actual placement when content is positioned (Radix may flip side); called with null when content unmounts. */
  onPlacementChange?: (side: PopoverPlacementSide | null) => void;
}

/** Matches the ui Select so portaled content clears every dialog and the Sheet. */
const POPOVER_PORTAL_Z_INDEX = 150;

export default function Popover({
  trigger,
  children,
  container,
  side = 'top',
  align = 'start',
  sideOffset,
  onOpenChange,
  onPlacementChange,
  usePortal = false,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const isMounted = useIsMounted();
  const observerRef = useRef<MutationObserver | null>(null);

  const closePopover = () => {
    setTimeout(() => {
      if (isMounted()) {
        setOpen(false);
      }
    }, 0);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Radix Dialog scroll lock can prevent wheel from reaching portaled content. Forward wheel to the scrollable list.
  const handleWheelCapture = useCallback((e: React.WheelEvent) => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !wrapper.contains(e.target as Node)) return;
    const candidates = wrapper.querySelectorAll(
      '[data-cmdk-list], [data-radix-select-viewport], .overflow-y-auto, .overflow-auto',
    );
    for (const node of candidates) {
      const el = node instanceof HTMLElement ? node : null;
      if (el && el.scrollHeight > el.clientHeight) {
        el.scrollTop += e.deltaY;
        e.preventDefault();
        e.stopPropagation();
        break;
      }
    }
  }, []);

  const contentRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      onPlacementChange?.(null);

      if (el) {
        const applySide = () => {
          const value = el.getAttribute('data-side') as PopoverPlacementSide | null;
          if (value) onPlacementChange?.(value);
        };
        applySide();
        const observer = new MutationObserver(applySide);
        observer.observe(el, { attributes: true, attributeFilter: ['data-side'] });
        observerRef.current = observer;
      }
    },
    [onPlacementChange],
  );

  const content = useMemo(() => {
    const _content = (
      <PopoverPrimitive.Content
        ref={contentRefCallback}
        sideOffset={sideOffset}
        side={side}
        align={align}
        className={usePortal ? 'pointer-events-auto' : undefined}
        style={usePortal ? { zIndex: POPOVER_PORTAL_Z_INDEX } : undefined}
      >
        {usePortal ? (
          <div ref={wrapperRef} className="pointer-events-auto min-w-0" onWheelCapture={handleWheelCapture}>
            {children(closePopover)}
          </div>
        ) : (
          children(closePopover)
        )}
      </PopoverPrimitive.Content>
    );
    if (usePortal) {
      return <PopoverPrimitive.Portal container={container ?? undefined}>{_content}</PopoverPrimitive.Portal>;
    }
    return _content;
  }, [
    usePortal,
    children,
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
    closePopover,
    sideOffset,
    side,
    align,
    container,
    contentRefCallback,
    handleWheelCapture,
  ]);

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        onOpenChange?.(isOpen);
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        {typeof trigger === 'function' ? trigger(open) : trigger}
      </PopoverPrimitive.Trigger>
      {content}
    </PopoverPrimitive.Root>
  );
}

interface PopoverContentProps {
  children?: React.ReactNode;
  className?: string;
}

export const PopoverContent = ({ children, className }: PopoverContentProps) => {
  return <div className={classNames('rounded-sm border bg-white p-2', className)}>{children}</div>;
};
